import React from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Alert,
  Paper,
  Divider,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
// MUI Icons
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedIcon from "@mui/icons-material/Verified";
import PublicIcon from "@mui/icons-material/Public";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MemoryIcon from "@mui/icons-material/Memory";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { getImageUrl } from "../../utils/helpers";
import InfoBox from "./utils/InfoBox";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";

// Import common components
import CustomAccordion from "../common/CustomAccordion";
import EmbededContactForm from "./EmbededContactForm";
import SetDocumentMeta from "../common/SetDocumentMeta";

// Feature card component for displaying key features or services
const FeatureCard = ({ title, icon, content, color = "#4ABDAC" }) => {
  return (
    <Card
      elevation={1}
      sx={{
        height: "100%",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box sx={{ color, mr: 1.5 }}>{icon}</Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color, fontWeight: "medium" }}
          >
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2">{content}</Typography>
      </CardContent>
    </Card>
  );
};

// Define "Jump to Section" items with icons
const jumpToSections = [
  {
    id: "tm-definition",
    label: "What is a Trademark?",
    icon: <InfoIcon sx={{ mr: 1 }} />,
  },
  {
    id: "tm-services",
    label: "Our Trademark Services",
    icon: <BusinessIcon sx={{ mr: 1 }} />,
  },
  {
    id: "tm-registration",
    label: "Trademark Registration Benefits",
    icon: <VerifiedIcon sx={{ mr: 1 }} />,
  },
  {
    id: "amazon-brand-protection",
    label: "Amazon Brand Protection",
    icon: <BrandingWatermarkIcon sx={{ mr: 1 }} />,
  },
  {
    id: "amazon-patent-disputes",
    label: "Amazon Patent Protection & Defense",
    icon: <GavelIcon sx={{ mr: 1 }} />,
    externalLink: "/amazon-patent-enforcement",
  },
  {
    id: "why-choose-us",
    label: "Why Choose Us",
    icon: <CheckCircleOutlineIcon sx={{ mr: 1 }} />,
  },
  {
    id: "contact-section",
    label: "Schedule a Free Consultation",
    icon: <PermContactCalendarIcon sx={{ mr: 1 }} />,
  },
];

const KthTM = ({ baseConfig }) => {
  const {
    priColor,
    priHoverColor,
    secColor,
    tertColor,
    tertHoverColor,
    quadColor,
    sextColor,
    linkColor,
  } = baseConfig.siteBranding;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // SEO metadata
  SetDocumentMeta({
    title: "Branding & Trademark Protection for Technology Companies",
    description:
      "Expert trademark services for technology and AI companies. We provide comprehensive trademark search, registration, and brand protection, especially for engineering and AI solutions. Schedule a consultation today.",
    keywords:
      "technology trademark attorney, \
      AI branding protection, \
      engineering trademark, \
      trademark search, \
      trademark registration, \
      Amazon brand registry, \
      trademark for AI companies, \
      brand protection services, \
      technology IP, \
      artificial intelligence branding, \
      software trademark, \
      trademark licensing, \
      trademark enforcement, \
      AI trademark protection, \
      Amazon brand protection",
  });

  // Custom styles
  const sectionStyles = {
    header: {
      py: 3,
      px: isMobile ? 2 : 4,
      mb: 4,
      borderRadius: 2,
      backgroundImage: `linear-gradient(135deg, ${tertColor}22 0%, ${tertColor}44 100%)`,
      boxShadow: "0 3px 5px rgba(0, 0, 0, 0.1)",
    },
    sectionTitle: {
      px: 2,
      py: 1.5,
      mb: 2,
      borderRadius: 1,
      backgroundColor: tertColor,
      color: "white",
      fontWeight: "medium",
      display: "flex",
      alignItems: "center",
    },
    card: {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      borderRadius: 2,
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        transform: "translateY(-2px)",
      },
    },
    accordionTitle: {
      fontWeight: "medium",
      color: "text.primary",
    },
  };

  // Function to scroll to a specific section
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Container sx={{ py: 4 }} className="link-no-decoration">
      {/* ======== PAGE HEADER ======== */}
      <Box component="header" sx={sectionStyles.header}>
        {/* Banner / Headline */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            color="text.primary"
          >
            Branding & Trademark Protection
          </Typography>
          <Typography
            variant="h6"
            fontWeight="medium"
            color="text.secondary"
            gutterBottom
          >
            Secure Your Brand's Future with Federal Trademark Registration
          </Typography>
          <Divider
            sx={{ my: 2, width: "20%", mx: "auto", borderColor: tertColor }}
          />
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            lineHeight: 1.8,
            textAlign: "center",
            maxWidth: "85%",
            mx: "auto",
            fontSize: "1.1rem",
          }}
        >
          <strong>In the rapidly evolving commercial space,</strong> your brand
          is often your most valuable asset. We specialize in trademark strategy
          for all organizations and particularly for engineering firms, software
          companies, and AI innovators. Our comprehensive trademark services
          help businesses establish, protect, and leverage their brand identity
          in an increasingly competitive marketplace.
        </Typography>
      </Box>

      {/* Banner for scheduling */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: linkColor,
          p: 2,
          borderRadius: 2,
          mt: 2,
          mb: 2,
          cursor: "pointer",
        }}
        onClick={() => {
          const ctaSection = document.getElementById("contact-section");
          if (ctaSection) {
            ctaSection.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        <PhoneInTalkIcon sx={{ mr: 1, color: "white" }} />
        <Typography variant="body1" sx={{ color: "white" }}>
          <strong>Schedule Your Free Brand Strategy Consultation</strong> to
          secure your brand's protection!
        </Typography>
      </Box>

      {/* ======== TABLE OF CONTENTS ======== */}
      <Card variant="outlined" sx={{ ...sectionStyles.card, mb: 4 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="medium"
            sx={{
              color: tertColor,
              borderBottom: `2px solid ${tertColor}`,
              pb: 1,
            }}
          >
            Quick Navigation
          </Typography>

          <List dense disablePadding>
            {jumpToSections.map((section) => (
              <ListItem key={section.id} disablePadding>
                {section.externalLink ? (
                  <ListItemButton
                    component={RouterLink}
                    to={section.externalLink}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: `${tertColor}22`,
                        pl: 2,
                      },
                    }}
                  >
                    <Box sx={{ color: tertColor }}>{section.icon}</Box>
                    <ListItemText primary={section.label} />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: `${tertColor}22`,
                        pl: 2,
                      },
                    }}
                  >
                    <Box sx={{ color: tertColor }}>{section.icon}</Box>
                    <ListItemText primary={section.label} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Fixed Fee AI Counseling Info Box */}
      <InfoBox
        title="SPECIAL OFFER: Discounted Fixed Fee Trademark Search and Registration"
        content={
          <Box>
            <Typography variant="body1" paragraph>
              We provide discounted fixed fee federal trademark search and
              filing. Whether you need a section 1(a) use in commerce, section
              1(b) intent to use (ITU), or foreign priority filing, we can
              represent you at the U.S.P.T.O at a predictable cost.
            </Typography>
            <Box display="flex" mt={2}>
              <Button
                startIcon={<PhoneInTalkIcon />}
                variant="contained"
                onClick={() => scrollToSection("contact-section")}
                sx={{
                  backgroundColor: linkColor,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: priHoverColor,
                  },
                }}
              >
                Request Free Quote & Strategy Consultation
              </Button>
            </Box>
          </Box>
        }
        type="primary"
        icon={<GavelIcon />}
      />

      {/* ======== MAIN CONTENT SECTIONS ======== */}

      {/* 1. What is a Trademark? */}
      <Box id="tm-definition" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <InfoIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              What is a Trademark?
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              A trademark is a distinctive sign, symbol, word, phrase, design,
              or combination thereof that identifies and distinguishes the
              source of goods or services of one party from those of others. In
              the technology sector, trademarks protect the valuable brands that
              represent your software, hardware, AI systems, and other
              innovative solutions.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Types of Trademarks
                  </Typography>
                  <List dense>
                    {[
                      "Word marks: Names, phrases, or slogans that identify your technology or service",
                      "Logos: Graphical elements or distinctive designs that represent your brand",
                      "Sound marks: Unique audio signatures associated with your technology",
                      "Trade dress: The visual appearance of your application interfaces or product packaging",
                      "Color marks: Distinctive colors associated with your brand in the marketplace",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Legal Protection Provided
                  </Typography>
                  <Typography variant="body2">
                    Trademarks provide legal protection against:
                  </Typography>
                  <List dense>
                    {[
                      "Unauthorized use of identical or similar marks that may cause consumer confusion",
                      "Dilution of your brand's distinctive quality or reputation",
                      "Unfair competition from businesses attempting to capitalize on your established brand equity",
                      "Domain squatting and brand impersonation in the digital space",
                      "Counterfeit products or services in physical and digital marketplaces",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <InfoBox
              title="Trademarks in Technology and AI"
              content="For engineering and AI companies, trademarks are especially critical as they protect the brand names of proprietary algorithms, software platforms, AI models, and technology solutions that may otherwise have limited intellectual property protection through patents or trade secrets alone."
              type="primary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 2. Our Trademark Services */}
      <Box id="tm-services" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <BusinessIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Our Trademark Services
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              We provide comprehensive trademark services tailored specifically
              for technology and AI businesses. Our expertise in both
              intellectual property law and technical domains allows us to
              develop trademark strategies that align with your business goals
              and technological innovations.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Trademark Search & Clearance"
                  icon={<SearchIcon />}
                  content="Comprehensive searches across USPTO databases, common law sources, and industry-specific platforms to identify potential conflicts with existing marks. For tech companies, we focus on software, SaaS, AI, and digital service classifications."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Trademark Registration"
                  icon={<GavelIcon />}
                  content="Full-service federal trademark application preparation and filing with the USPTO. We handle all aspects from initial application through addressing office actions and final registration for your technology brand."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="International TM Protection"
                  icon={<PublicIcon />}
                  content="Strategic international trademark registration through the Madrid Protocol or direct country filings to protect your technology brand in global markets where you operate, develop, or plan to expand."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Portfolio Management"
                  icon={<BusinessIcon />}
                  content="Ongoing management of your technology trademark portfolio, including renewals, maintenance filings, and strategic updates as your product offerings and services evolve in the rapidly changing tech landscape."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Trademark Enforcement"
                  icon={<SecurityIcon />}
                  content="Monitoring and enforcement against infringement, including cease and desist letters, UDRP proceedings for domain disputes, and marketplace takedown notices for counterfeit software or AI services."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Trademark Licensing"
                  icon={<HandshakeIcon />}
                  content="Strategic trademark licensing agreements that allow you to monetize your brand while maintaining quality control, particularly important for API licensing, software integration, and technology partnerships."
                  color={tertColor}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Technology-Specific Trademark Considerations
              </Typography>
              <Typography variant="body2" paragraph>
                Our trademark services are specifically tailored to address the
                unique challenges faced by technology companies:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Strategic Classification
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Strategic selection of appropriate Nice Classification
                        categories for software, AI, machine learning, data
                        analytics, and other technical goods and services.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Digital Protection Strategy
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Comprehensive digital protection strategy including
                        domain name acquisition, social media handles, and app
                        store listings to secure your brand across all digital
                        touchpoints.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Descriptiveness Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Assessment of technical terms in your mark for potential
                        descriptiveness issues in USPTO applications, with
                        strategies to overcome rejections based on technical
                        terminology.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI-Generated Brand Asset Protection
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Specialized protection strategies for AI-generated
                        logos, sounds, and other brand assets with consideration
                        for emerging legal frameworks around AI-created content.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Brand Licensing Agreements"
              content="Strategic trademark licensing can be a powerful revenue stream for companies, allowing you to monetize your brand while maintaining quality control."
              type="primary"
              link="/legal-agreements-for-ai"
              linkText="Learn More"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 3. Trademark Registration Benefits */}
      <Box id="tm-registration" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <VerifiedIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Benefits of Trademark Registration
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              While common law trademark rights exist from the first use in
              commerce, federal registration with the USPTO provides significant
              additional benefits that are particularly valuable for companies.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Federal Registration Advantages
                  </Typography>
                  <List dense>
                    {[
                      "Legal presumption of ownership and exclusive right to use the mark nationwide",
                      "Public notice of your claim of ownership through the USPTO's database",
                      "Right to use the ® symbol, signaling stronger protection to competitors",
                      "Ability to register with U.S. Customs to prevent importation of infringing foreign goods",
                      "Federal court jurisdiction for infringement actions with potential for enhanced damages",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Types of USPTO Registration
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Section 1(a) - Use in Commerce"
                        secondary="For marks already in commercial use with your technology products or services. Requires evidence of use with specific specimens."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Section 1(b) - Intent to Use"
                        secondary="For technology products or services under development. Reserves rights before market launch, with up to 3 years to demonstrate commercial use."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                      <ListItemText
                        primary="Section 44(d)/44(e) - Foreign Priority"
                        secondary="For technology companies that have previously registered internationally and wish to extend protection to the U.S. market."
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <InfoBox
              title="Common Law vs. Registered Trademark Rights"
              content="While common law rights protect your mark in limited geographic areas where you operate, registered rights extend nationwide—crucial for software and AI companies with digital products and services that inherently have a national reach. Registration provides significantly stronger protection against infringement and counterfeiting in the digital marketplace."
              type="warning"
            />

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={tertColor}
                fontWeight="bold"
              >
                Strategic Considerations for Registration
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <MemoryIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Rapidly Evolving Products
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We help establish sufficiently broad protection for your
                        core brand while your technology evolves through version
                        updates and expanding functionality.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <LightbulbIcon
                      sx={{ color: tertColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Sub-Branding Strategy
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Strategic approach for protecting your main company
                        brand alongside feature-specific sub-brands and AI tool
                        names that comprise your product ecosystem.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* 4. Amazon Brand Registry Section */}
      <Box id="amazon-brand-protection" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <StorefrontIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Amazon Brand Protection
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              For companies selling hardware, devices, or physical products on
              Amazon, brand protection is essential to combat counterfeit goods
              and unauthorized sellers. Our Amazon Brand Registry services
              provide robust protection for your listings in the world's largest
              online marketplace.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#f5f7fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: tertColor }}
                  >
                    Amazon Brand Registry Benefits
                  </Typography>
                  <List>
                    {[
                      "Greater control over product listings and enhanced content with A+ Content for your technology products",
                      "Proactive brand protection with Amazon's automated systems that identify and remove suspected infringing listings",
                      "Access to Brand Analytics providing valuable market insights for your technology category",
                      "Streamlined infringement reporting through the Report a Violation tool",
                      "Enhanced advertising options like Sponsored Brands and Stores to showcase your technology brand",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    backgroundColor: "#f5f7fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: tertColor }}
                  >
                    Our Amazon Brand Protection Services
                  </Typography>
                  <List>
                    {[
                      "Trademark registration specifically prepared to meet Amazon Brand Registry requirements",
                      "Assistance with the entire Brand Registry enrollment process",
                      "Strategic guidance on how to optimize your brand presence on Amazon",
                      "Support with infringement issues, including removal of counterfeit tech products",
                      "Guidance on Amazon's IP Accelerator program for expedited brand protection",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: tertColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <InfoBox
              title="Integration with Patent Enforcement"
              content="For technology products that also have patent protection, our integrated approach combines trademark enforcement through Amazon Brand Registry with patent enforcement strategies. This multi-layered protection is especially valuable for innovative tech hardware and devices."
              type="primary"
              link="/amazon-patent-enforcement"
              linkText="Learn More"
            />

            {/* Amazon Patent Enforcement Container */}
            <Grid container spacing={2} alignItems="stretch" sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    borderLeft: `6px solid #FF9900`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "100%",
                    p: 2, // Reduced padding for compact design
                  }}
                >
                  <CardContent sx={{ width: "100%", p: 0 }}>
                    {/* Amazon Logo + Headline (Same Row) */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ backgroundColor: "#fff", p: 1 }}
                    >
                      <img
                        src={getImageUrl("amz-pat-disputes.png")}
                        alt="Amazon Patent Disputes"
                        style={{
                          width: "180px",
                          height: "auto",
                          marginRight: 10,
                        }}
                      />
                      <Typography variant="h6" fontWeight="bold">
                        Patent Enforcement
                      </Typography>
                    </Box>

                    {/* Content Section with Background */}
                    <Box sx={{ backgroundColor: "#f8f9fa", p: 2 }}>
                      <Grid container spacing={2} justifyContent="center">
                        {/* Patent Owners Column */}
                        <Grid item xs={12} sm={6}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <GavelIcon sx={{ color: "#28a745", mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              For Patent Owners
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            Enforce your patents against unauthorized Amazon
                            sellers.
                          </Typography>
                        </Grid>

                        {/* Amazon Sellers Column */}
                        <Grid item xs={12} sm={6}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <ShoppingCartIcon
                              sx={{ color: "#FF9900", mr: 1 }}
                            />
                            <Typography variant="subtitle1" fontWeight="bold">
                              For Amazon Sellers
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            Defend your listings from unfair patent infringement
                            claims.
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* CTA Button (Reduced Margin for Compact Design) */}
                    <Box textAlign="left" sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: tertColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: tertHoverColor,
                            color: "#fff",
                          },
                        }}
                        component={RouterLink}
                        to="/amazon-patent-enforcement"
                        endIcon={<ArrowForwardIosIcon />}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* 5. Why Choose Us */}
      <Box id="why-choose-us" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${tertColor}22`,
            }}
          >
            <CheckCircleOutlineIcon sx={{ mr: 2, color: tertColor }} />
            <Typography variant="h6" fontWeight="bold">
              Why Choose Us for Your Technology Trademark Needs
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Our firm brings a unique combination of intellectual property
              expertise and deep understanding of engineering and technology
              industries. This dual focus allows us to provide trademark
              services that are specifically tailored to the needs of technology
              companies.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Technology Industry Focus"
                  icon={<MemoryIcon />}
                  content="We specialize in working with engineering firms, software developers, and AI companies. Our deep understanding of the technology sector means we can provide more relevant and effective trademark strategies than general practice firms."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Integrated IP Approach"
                  icon={<BusinessIcon />}
                  content="We offer comprehensive intellectual property services, including patents and trade secrets, allowing for a holistic protection strategy. This integration is particularly valuable for technology companies with diverse IP portfolios."
                  color={tertColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="AI Technology Expertise"
                  icon={<LightbulbIcon />}
                  content="With specific experience in AI-related trademark issues, we understand the unique challenges of protecting AI brand assets, algorithm names, and interface elements that define your AI product's identity in the marketplace."
                  color={tertColor}
                />
              </Grid>
            </Grid>

            <Box
              sx={{ p: 3, mt: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: tertColor }}>
                Our Full-Service IP Approach
              </Typography>
              <Typography variant="body2" paragraph>
                By choosing our firm, you gain access to comprehensive
                intellectual property services, including:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <GavelIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        Patents
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Strategic patent protection for your core technological
                      innovations
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/patents"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn MOre
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <HandshakeIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        AI Agreements
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Specialized legal agreements for AI development and
                      licensing
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/legal-agreements-for-ai"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn MOre
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <SecurityIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        Amazon Enforcement
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Protection against infringement on the Amazon marketplace
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/amazon-patent-enforcement"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn MOre
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <LightbulbIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        AI Services
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Specialized legal counsel for artificial intelligence
                      companies
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/ai"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn MOre
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Trademark Licensing and Commercialization"
              content="Our experience with IP licensing means we can help you not only protect your technology brand but also effectively monetize it through strategic licensing agreements, technology partnerships, and other commercial opportunities."
              type="success"
              link="/legal-agreements-for-ai"
              linkText="Learn More"
            />
          </CardContent>
        </Card>
      </Box>

      {/* ======== CONTACT / CTA SECTION ======== */}
      <Box
        component="section"
        id="contact-section"
        sx={{
          mt: 0,
          p: 4,
          borderRadius: 2,
          backgroundColor: "#f3f6f9",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${tertColor}33`,
        }}
      >
        {/* Title spanning both columns */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight="bold" color={tertColor}>
            Ready to Protect Your Brand?
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            For Immediate Assistance, Call Us At:{" "}
            <Chip
              label="+1-602-837-0631"
              color="primary"
              sx={{
                backgroundColor: tertColor,
                fontWeight: "bold",
                fontSize: "1rem",
                height: "32px",
                "& .MuiChip-label": {
                  color: "white",
                },
              }}
            />
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT SIDE: Image + Overview */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: tertColor, mb: 2 }}
              >
                What to Expect During Your Consultation:
              </Typography>

              <List>
                {[
                  "An assessment of your current brand assets and trademark needs",
                  "Guidance on strategies for protecting your brand",
                  "An explanation of the trademark process, timeline, and investment",
                  "Insights on how trademark protection integrates with your other IP strategies",
                ].map((text, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <CheckCircleOutlineIcon
                      sx={{ color: tertColor, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {text}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={tertColor}
                >
                  Our Promise to Our Clients
                </Typography>
                <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                  We understand that protecting your brand is crucial. Our goal
                  is to provide clear, actionable advice and efficient trademark
                  services that integrate with your overall business and IP
                  strategy. No unnecessary legal jargon—just practical solutions
                  tailored to your brand.
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT SIDE: Contact Form */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: tertColor, color: "white" }}>
                <Typography variant="h5" fontWeight="medium" align="center">
                  Schedule Your Free Trademark Consultation
                </Typography>
              </Box>
              <CardContent>
                <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
                  Please complete the form below, and our team will reach out to
                  schedule a consultation with a trademark attorney.
                </Typography>

                <EmbededContactForm
                  dropDownOptions={[
                    "New Trademark Registration",
                    "Trademark Search & Clearance",
                    "Amazon Brand Registry",
                    "International Trademark Protection",
                    "Trademark Enforcement",
                    "Trademark Portfolio Management",
                    "Trademark Licensing",
                    "Other Trademark Matter",
                  ]}
                  submitButtonLabel="Schedule My Free Consultation"
                  disclaimerHTML="Please note that submitting this form does not create an attorney-client relationship. Any information shared will be kept confidential."
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* ======== FINAL DISCLAIMER ======== */}
      <Box
        // textAlign="center"
        mt={6}
        pt={2}
        borderTop="1px solid #ccc"
        sx={{
          backgroundColor: "#f9f9f9",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          Disclaimer: Please do not share confidential details until we confirm
          our representation. This website provides general information and is
          not intended as legal advice for your specific situation.
        </Typography>
      </Box>
    </Container>
  );
};

export default KthTM;
